import React, {useState, useEffect} from 'react';
import {
    Box,
    Paper,
    Typography,
    TextField,
    Alert,
    Fade
} from '@mui/material';
import { ContentPaste as PasteIcon } from '@mui/icons-material';

interface PasteAreaProps {
    onUpload: (data: string) => void;
    isUploading: boolean;
    uploadError?: string;
    children?: React.ReactNode
}

export const PasteArea: React.FC<PasteAreaProps> = ({
    onUpload,
    children,
    isUploading,
    uploadError}) => {
    const filler = children === undefined || children === null;
    const [showSuccess, setShowSuccess] = useState(false);
    const [fadeSuccess, setFadeSuccess] = useState(false);

    useEffect(() => {
        if (showSuccess) {
            setTimeout(() => setFadeSuccess(true), 200);
        } else {
            setTimeout(() => setFadeSuccess(false), 200);
        }
    }, [showSuccess]);

    useEffect(() => {
        const listener = (e: Event) => {
            let data = (e as ClipboardEvent).clipboardData!.getData("text").trim();
            if (data) {
                onUpload(data);
            }
            setShowSuccess(true);
            setTimeout(() => setShowSuccess(false), 3000);
        }
        window.addEventListener("paste", listener);

        return () => window.removeEventListener("paste", listener);
    }, [onUpload]);

    return (
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            {/* Upload Status */}
            {uploadError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {uploadError}
                </Alert>
            )}

            {(showSuccess || fadeSuccess) && <Fade in={fadeSuccess}>
                <Alert severity="success" sx={{ mb: 2 }}>
                    SimC report uploaded successfully!
                </Alert>
            </Fade>}

            {filler ? <Paper
                elevation={2}
                sx={{
                    flex: 1,
                    p: 3,
                    display: 'flex',
                    flexDirection: 'column',
                    borderRadius: 2,
                    border: '2px dashed',
                    borderColor: 'divider',
                    '&:hover': {
                        borderColor: 'primary.main',
                        backgroundColor: 'action.hover'
                    }
                }}
                tabIndex={0}
            >
                <Box sx={{ textAlign: 'center', mb: 3 }}>
                    <PasteIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                    <Typography variant="h6" gutterBottom>
                        Paste SimC Data
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Paste anywhere in this area or click the paste button below
                    </Typography>
                </Box>

                <TextField
                    multiline
                    minRows={10}
                    maxRows={20}
                    placeholder="Paste your SimulationCraft character data here..."
                    value={""}
                    variant="outlined"
                    fullWidth
                    sx={{
                        flex: 1,
                        '& .MuiOutlinedInput-root': {
                            height: '100%',
                            alignItems: 'flex-start'
                        }
                    }}
                />
            </Paper> : <>{children}</>}
        </Box>
    );
};